import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import MultiStepForm from "./MultiStepForm";
import Loading from "Components/Loading";
import useSession from "Utils/Hooks/useSession";
import useMode, { MODE } from "Utils/Hooks/useMode";
import useFundType, { FUNDTYPE } from "Utils/Hooks/useFundType";
import "./Enrollment.scss";
import useVipApiFetcher from "Utils/Hooks/useVipApiFetcher";
import useSessionStorage from "Utils/Hooks/useSessionStorage";
import { PRODUCTTYPES } from "Utils/Constants";
import { faro, LogLevel } from "@grafana/faro-react";
import { Box } from "@pav/pav-products-design-system";
import { useMediaQuery, useTheme } from "@mui/material";

const Enrollment = (props) => {
  const { session, fetchSession, updateSession } = useSession();
  const [showTerms, setShowTerms] = useSessionStorage("vip-showTerms");
  const vipApi = useVipApiFetcher();
  const location = useLocation();
  const history = useHistory();
  const [submitted, setSubmitted] = useState(false);
  const mode = useMode();
  const fundType = useFundType();
  const [mitekRedirect, setMitekRedirect] = useState(false);
  const [mitekLink, setMitekLink] = useState(null);
  const refTimeOut = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isNetspend = fundType === FUNDTYPE.netspend;

  const navigateToDeposit = mode === MODE.deposit;
  const navigateToWithdraw = mode === MODE.withdraw;

  const productTypeTitle =
    session?.patronProductType === PRODUCTTYPES.PREFERRED ? "VIPP" : "VIPO";
  document.title = productTypeTitle + " - Enroll";

  const [show, setShow] = useState(isNetspend);

  const handleClose = () => setShow(false);
  const {
    fromOperator,
    formData,
    message = "",
  } = { ...props, ...location.state };

  const style = {
    width: "100%",
    height: "66vh",
    overflow: "hidden",
    border: "none",
    align: "center",
  };

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === "timeout") {
        refTimeOut.current = true;
      }
    };
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [mitekRedirect]);

  if (submitted) return <Loading isEnrollment={true} />;

  const formatData = (formData, skipKycCheck = false) => {
    let formatted = { ...formData };
    if (session.toggles.identificationInfoEnabled && props.plaidRedirect) {
      let idInfo = JSON.parse(localStorage.getItem("id-info"));
      formatted.ssn = idInfo.ssn?.replaceAll("-", "");
      formatted.idExpiration = idInfo.idExpiration?.replaceAll("/", "");
      formatted.country = idInfo.country;
      formatted.idNumber = idInfo.idNumber;
      formatted.idState = idInfo.idState;
      formatted.idType = idInfo.idType;
    } else {
      formatted.ssn = formatted.ssn?.replaceAll("-", "");
      formatted.idExpiration = formatted.idExpiration?.replaceAll("/", "");
    }
    formatted.mobilePhone = formatted.mobilePhone?.replaceAll("-", "");
    formatted.skipKycCheck = skipKycCheck;
    formatted.IsPavilion = window.location.href.includes("paviliononline");

    return formatted;
  };

  const poll = async function (ms) {
    let result = await vipApi.fetch("/api/mitek/status");
    while (result.status === 202 && !refTimeOut.current) {
      updateSession(await result.json());
      await wait(ms);
      result = await vipApi.fetch("/api/mitek/status");
    }
    return result;
  };

  const wait = function (ms = 5000) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  };

  async function submit(formData) {
    setSubmitted(true);
    const formatted = formatData(formData);
    const response = await vipApi.fetch("/api/accounts", "POST", formatted);
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      setShowTerms(false);
      const body = await response.json();
      if (body.status) {
        if (body.status === "failure") {
          onFailure(body);
        } else if (body.status === "failureRetry") {
          if (session.toggles.mitekEnabled && body.mitekResponse) {
            if (response && response.status === 202) {
              setMitekRedirect(true);
              setMitekLink(body.mitekResponse.link);
              setSubmitted(false);
              let responseCode = await poll(10000);
              if (responseCode.status === 200) {
                submitOnMitekSuccess(formData);
              } else {
                onEnrollError(body);
              }
            } else {
              onEnrollError(body);
            }
          } else {
            onFailureRetry(body);
          }
        } else if (body.status === "success") {
          onEnrollSuccess();
        } else if (body.status === "noBanks") {
          await fetchSession(true);
          setSubmitted(false);
          history.push({ pathname: "/addAccount", state: { nobank: true } });
        } else if (body.status === "partial") {
          onPartialRetry(body);
        }
      } else {
        onFailure(body);
      }
      // Error condition - content type not JSON
    } else {
      onEnrollError("Enrollment Response is not JSON");
    }
  }

  async function submitOnMitekSuccess(formData) {
    const formatted = formatData(formData, true);
    const response = await vipApi.fetch("/api/accounts", "POST", formatted);
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      setShowTerms(false);
      const body = await response.json();
      if (body.status) {
        if (body.status === "failure") {
          onFailure(body);
        } else if (body.status === "failureRetry") {
          onFailureRetry(body);
        } else if (body.status === "success") {
          onEnrollSuccess();
        } else if (body.status === "noBanks") {
          await fetchSession(true);
          setSubmitted(false);
          history.push({ pathname: "/addAccount", state: { nobank: true } });
        } else if (body.status === "partial") {
          onPartialRetry(body);
        }
      } else {
        onFailure(body);
      }
      // Error condition - content type not JSON
    } else {
      //const body = await response.text();
      onEnrollError("Mitek Success Enrollment Response is not JSON");
    }
  }

  async function onEnrollSuccess() {
    // Fetch updated session data - should now show as enrollment complete.
    await fetchSession(true);
    setSubmitted(false);
    faro.api.pushLog(["Successful Enrollment - Account Created"], {
      level: LogLevel.INFO,
    });
    history.push({
      pathname: "/submit",
      state: {
        isEnrollment: true,
        isSuccess: true,
        isError: false,
        title: "Account Created",
        message:
          "Your VIP account has been created and you are now able to make transactions.",
        // title: "Enrollment Complete",
        // message: "You may now make transactions with your new VIP Preferred account.",
        navigateTo:
          mode === MODE.manageBanks
            ? "myAccounts"
            : navigateToDeposit
              ? "addFunds"
              : navigateToWithdraw
                ? "withdrawFunds"
                : "dashboard",
      },
    });
  }

  function onFailure(body) {
    setSubmitted(false);
    faro.api.pushError(
      new Error(
        `Enrollment Failure - ${body?.errorModel?.patronErrorMessage ? body?.errorModel?.patronErrorMessage : body}`,
      ),
    );
    history.push({
      pathname: "/submit",
      state: {
        isEnrollment: true,
        isSuccess: false,
        isError: true,
        fromOperator,
        formData,
        message: body.errorModel.patronErrorMessage
          ? body.errorModel.patronErrorMessage
          : body,
        customButtons: [
          {
            label: `Continue To ${session.operatorName}`,
            returnToOperator: true,
            returnUrl: session?.returnUrl,
            transactionId: session?.transactionId,
            transactionType:
              mode === MODE.manageBanks
                ? "bankManagement"
                : navigateToWithdraw
                  ? "withdraw"
                  : "deposit",
          },
        ],
        onClickClose: true,
      },
    });
  }

  function onFailureRetry(body) {
    setSubmitted(false);
    faro.api.pushError(new Error("Enrollment Failure Retry"));
    history.push({
      pathname: "/retry",
      state: {
        isEnrollment: true,
        isSuccess: false,
        isError: true,
        fromOperator: body.enrollmentRequirements ?? fromOperator,
        formData,
        message: body.errorModel.patronErrorMessage,
      },
    });
  }

  function onPartialRetry(body) {
    setSubmitted(false);
    faro.api.pushError(
      new Error("Enrollment Partial Retry - Missing Information"),
      { context: body },
    );
    history.push({
      pathname: "/retry",
      state: {
        isEnrollment: true,
        isSuccess: false,
        isError: true,
        fromOperator: body.enrollmentRequirements,
        formData,
        message: "Missing information",
      },
    });
  }

  function onEnrollError(body) {
    faro.api.pushError(new Error("Enrollment Error - Account Not Created"), {
      context: body,
    });
    setSubmitted(false);
    history.push({
      pathname: "/submit",
      state: {
        isEnrollment: true,
        isSuccess: false,
        isError: true,
        onClickClose: true,
        title: "Account Not Created",
        message:
          "We are unable to complete this request. Please contact our Customer Service Team at 1-800-500-1973. Reference code E-28 so that we may better serve you.",
        buttonText: `Continue To ${session.operatorName}`,
      },
    });
  }

  const multiStepFormProps = {
    fromOperator,
    defaultData: formData,
    submit,
    errorMessage: message,
    isEnrollment: true,
    plaidRedirect: props.plaidRedirect,
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        className="header"
        sx={{
          marginBottom: "var(--spacing-md)",
          textAlign: isMobile ? "left" : "center",
        }}
      >
        Account Sign Up
      </Box>
      {!mitekRedirect ? (
        <MultiStepForm {...multiStepFormProps} />
      ) : (
        <div style={style}>
          <iframe
            src={mitekLink}
            allow="autoplay; camera; microphone"
            title="Verification Process"
            style={style}
          />
        </div>
      )}
    </Box>
  );
};

export default Enrollment;
